import { buildScopeTranslate } from "lib/I18n";
import { ATTEMPT_CONFLICT_CODES, GRADING_ERROR_CODES, MS_PER_SECOND } from "./constants";

const tErrors = buildScopeTranslate("views.content.assessment_framework.assessments.errors");

const KNOWN_GRADING_ERROR_CODES = new Set(Object.values(GRADING_ERROR_CODES));

// The poll body is { status: "error", error: { code, retryable, retry_after_seconds }, regrade_href }.
// regrade_href carries no method, so a retry is always a POST to it. retry_after_seconds is
// relative to the response, so the deadline is fixed at receipt time. A missing or null
// value is never treated as 0: the learner gets no Retry.
export function gradingErrorFromAttempt(attempt, receivedAt = Date.now()) {
  const nested = attempt?.error;
  const waitSeconds = nested?.retry_after_seconds;
  const canRetry = Boolean(attempt?.regrade_href)
    && nested?.retryable === true
    && Number.isFinite(waitSeconds)
    && waitSeconds >= 0;

  return {
    code: nested?.code,
    retry: canRetry ? { href: attempt.regrade_href, method: "POST" } : undefined,
    retryAvailableAt: canRetry ? receivedAt + waitSeconds * MS_PER_SECOND : null,
  };
}

export function retryWaitSeconds(gradingError, now = Date.now()) {
  if (!gradingError?.retry) {
    return 0;
  }

  return Math.max(0, Math.ceil((gradingError.retryAvailableAt - now) / MS_PER_SECOND));
}

// Whether a refreshed error differs from the one on screen enough to redraw. A tab that
// sat in the background past its deadline still reads "no wait left" on both sides.
export function gradingErrorChanged(shown, refreshed, now = Date.now()) {
  if (shown.code !== refreshed.code || shown.retry?.href !== refreshed.retry?.href) {
    return true;
  }

  const shownWait = retryWaitSeconds(shown, now);
  const refreshedWait = retryWaitSeconds(refreshed, now);

  return (shownWait > 0 || refreshedWait > 0) && Math.abs(shownWait - refreshedWait) > 1;
}

// A held error is a fault ops must fix, so its wait is too long to count down.
export function isHeldError(gradingError) {
  return gradingError?.code === GRADING_ERROR_CODES.GRADING_UNAVAILABLE;
}

export function gradingErrorMessage(error) {
  const code = error?.code;
  if (KNOWN_GRADING_ERROR_CODES.has(code)) {
    return tErrors(code);
  }

  return tErrors("grading_failed");
}

// A held error keeps its code after the wait ends: only a regrade clears it. So it is held
// from the learner's side only while its wait runs; after that Retry is theirs to use.
export function isHeldWait(gradingError, now = Date.now()) {
  return isHeldError(gradingError) && retryWaitSeconds(gradingError, now) > 0;
}

// A held wait promises a later retry; an error with a Retry invites the learner to use it.
// Anything else has nothing to promise, so it stays silent.
export function gradingErrorToast(gradingError) {
  if (isHeldWait(gradingError)) {
    return tErrors("grading_unavailable_toast");
  }

  return gradingError.retry ? tErrors("grading_retryable_toast") : null;
}

export function routesAwayFromError(attempt) {
  const code = attempt?.error?.code;
  return code === ATTEMPT_CONFLICT_CODES.ALREADY_PASSED || code === ATTEMPT_CONFLICT_CODES.ATTEMPTS_EXHAUSTED;
}
